import { githubCache } from '../cache'
import { GITHUB_REPO, resolveBranch } from '../github.const'
import type { GitHubFile } from '../github.interface'

import { fetchRepositoryTree } from './fetchRepositoryTree'

const GITHUB_FILE_TYPE_DIR = 'dir'

const README_FILE_NAMES = new Set(['readme.md', 'readme.mdx'])

function isIncludedMarkdownFile(file: GitHubFile): boolean {
  if (!file.name.endsWith('.md') && !file.name.endsWith('.mdx')) return false
  return !README_FILE_NAMES.has(file.name.toLowerCase())
}

async function collectMarkdownFilesFromTree(
  branch: string,
  directory: string,
  bypassNextCache: boolean,
  repo: string
): Promise<GitHubFile[]> {
  const files = await fetchRepositoryTree(branch, directory, repo)
  const markdownFiles: GitHubFile[] = []

  for (const file of files) {
    if (file.type === GITHUB_FILE_TYPE_DIR) {
      const subFiles = await getAllMarkdownFiles(branch, file.path, bypassNextCache, repo)
      markdownFiles.push(...subFiles)
    } else if (isIncludedMarkdownFile(file)) {
      markdownFiles.push(file)
    }
  }

  return markdownFiles
}

function mergeDirectoryFilesIntoRootCache(
  rootCacheKey: string,
  directory: string,
  freshDirectoryFiles: GitHubFile[]
): GitHubFile[] {
  if (directory === '') return freshDirectoryFiles

  const existingRootCache = githubCache.getFileList(rootCacheKey)
  if (!existingRootCache?.files) return freshDirectoryFiles

  const filesOutsideDirectory = existingRootCache.files.filter(
    (file: GitHubFile) => !file.path.startsWith(`${directory}/`) && file.path !== directory
  )

  return [...filesOutsideDirectory, ...freshDirectoryFiles].toSorted((a, b) => a.path.localeCompare(b.path))
}

export async function getAllMarkdownFiles(
  branch: string | null = null,
  directory: string = '',
  bypassNextCache: boolean = false,
  repo: string = GITHUB_REPO
): Promise<GitHubFile[]> {
  const resolvedBranch = resolveBranch(branch, repo)
  const rootCacheKey = `filelist:${repo}:${resolvedBranch}:root`

  if (!bypassNextCache) {
    const rootCached = githubCache.getFileList(rootCacheKey)
    const cachedFiles = rootCached?.files
    if (cachedFiles && cachedFiles.length > 0) {
      if (directory === '') return cachedFiles as GitHubFile[]
      return cachedFiles.filter(
        (file: GitHubFile) => file.path.startsWith(`${directory}/`) || file.path === directory
      ) as GitHubFile[]
    }
  }

  const markdownFiles = await collectMarkdownFilesFromTree(resolvedBranch, directory, bypassNextCache, repo)
  const sortedFiles = markdownFiles.toSorted((a, b) => a.path.localeCompare(b.path))
  const filesToCache = mergeDirectoryFilesIntoRootCache(rootCacheKey, directory, sortedFiles)

  githubCache.setFileList(rootCacheKey, filesToCache)
  return sortedFiles
}
